import * as React from "react";
import { Link } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Container,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import logo from "../../assets/logo.png";

const dropdownMenus = {
  services: [
    {
      name: "SAP Consulting & Implementation",
      path: "/services/sap-consulting-implementation",
    },
    {
      name: "SAP Support & Managed Services",
      path: "/services/sap-support-managed-services",
    },
    {
      name: "SAP Upgrade & Migration Services",
      path: "/services/sap-upgrade-migration-services",
    },
    {
      name: "Digital Transformation",
      path: "/services/digital-transformation",
    },
    {
      name: "Cloud Services",
      path: "/services/cloud-services",
    },
    {
      name: "Custom Application Development",
      path: "/services/custom-application-development",
    },
    {
      name: "Data Analytics & Business Intelligence",
      path: "/services/data-analytics-business-intelligence",
    },
    {
      name: "AI, IoT & Intelligent Automation",
      path: "/services/ai-iot-intelligent-automation",
    },
  ],
  products: [
    {
      name: "Vendor Management Portal",
      path: "/products/vendor-management-portal",
    },
    {
      name: "Business Process Automation",
      path: "/products/business-process-automation",
    },
    {
      name: "Automated Digital Signature Tool in SAP",
      path: "/products/automated-digital-signature-tool-in-sap",
    },
    {
      name: "Customer & Pricing Management",
      path: "/products/customer-pricing-management",
    },
  ],
};

const mainLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
];

const navButtonSx = {
  color: "#fff",
  px: 1.8,
  py: 1.2,
  fontSize: "0.95rem",
  fontWeight: 500,
  textTransform: "none",
  borderRadius: "14px",
  position: "relative",
  transition: "all 0.35s ease",

  "&::after": {
    content: '""',
    position: "absolute",
    left: "18%",
    bottom: 4,
    width: "0%",
    height: "3px",
    borderRadius: "20px",
    background: "linear-gradient(90deg, #26c65b, #00e5ff)",
    transition: "width 0.35s ease",
  },

  "&:hover": {
    background: "rgba(255,255,255,0.08)",
    transform: "translateY(-2px)",
  },

  "&:hover::after": {
    width: "64%",
  },
};

const premiumMenuPaperSx = {
  mt: 1.8,
  minWidth: 380,
  p: 1,
  borderRadius: "24px",
  overflow: "hidden",
  color: "#fff",
  background: "linear-gradient(145deg, rgba(7,21,37,0.98), rgba(8,45,99,0.96))",
  backdropFilter: "blur(22px)",
  border: "1px solid rgba(255,255,255,0.14)",
  boxShadow: "0 30px 80px rgba(0,0,0,0.45)",

  "& .MuiMenu-list": {
    p: 0,
  },

  "& .MuiMenuItem-root": {
    borderRadius: "16px",
    px: 2,
    py: 1.45,
    mb: 0.5,
    gap: 1.4,
    fontSize: "0.95rem",
    fontWeight: 500,
    whiteSpace: "normal",
    transition: "all 0.28s ease",
  },

  "& .MuiMenuItem-root::before": {
    content: '""',
    width: 9,
    height: 9,
    borderRadius: "50%",
    flexShrink: 0,
    background: "linear-gradient(135deg, #26c65b, #00e5ff)",
    boxShadow: "0 0 14px rgba(38,198,91,0.55)",
  },

  "& .MuiMenuItem-root:hover": {
    color: "#8dffbd",
    transform: "translateX(6px)",
    background:
      "linear-gradient(135deg, rgba(38,198,91,0.16), rgba(0,229,255,0.10))",
  },
};

const mobileNavItemSx = {
  borderRadius: 3,
  mb: 0.8,
  color: "#fff",

  "&:hover": {
    background: "rgba(255,255,255,0.08)",
  },
};

const mobileDropdownItemSx = {
  pl: 4,
  borderRadius: 3,
  color: "rgba(255,255,255,0.82)",

  "&:hover": {
    color: "#7dffb0",
    background: "rgba(38,198,91,0.12)",
  },
};

const smartSapButtonSx = {
  ml: 2,
  px: 2.2,
  py: 1,
  color: "#fff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  borderRadius: "14px",
  background: "linear-gradient(135deg, #26c65b, #00bcd4)",
  boxShadow: "0 10px 26px rgba(38,198,91,0.28)",
  transition: "all 0.3s ease",

  "&:hover": {
    background: "linear-gradient(135deg, #22b653, #00acc1)",
    transform: "translateY(-2px)",
    boxShadow: "0 14px 32px rgba(38,198,91,0.35)",
  },

  "& .MuiButton-startIcon": {
    color: "#fff",
  },
};

export default function Header() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileDropdowns, setMobileDropdowns] = React.useState({
    services: false,
    products: false,
  });

  const [menuAnchor, setMenuAnchor] = React.useState({
    key: null,
    element: null,
  });

  const [isScrolled, setIsScrolled] = React.useState(false);

  const desktopMenuOpen = Boolean(menuAnchor.element);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openDesktopMenu = (key, event) => {
    setMenuAnchor({
      key,
      element: event.currentTarget,
    });
  };

  const closeDesktopMenu = () => {
    setMenuAnchor({
      key: null,
      element: null,
    });
  };

  const toggleMobileDropdown = (key) => {
    setMobileDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const closeDrawer = () => {
    setMobileOpen(false);
    setMobileDropdowns({
      services: false,
      products: false,
    });
  };

  const renderDesktopDropdownButton = (label, key) => (
    <React.Fragment key={key}>
      <Button
        sx={navButtonSx}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={(event) => openDesktopMenu(key, event)}
      >
        {label}
      </Button>
    </React.Fragment>
  );

  const renderMobileDropdown = (label, key) => (
    <React.Fragment key={key}>
      <ListItemButton
        onClick={() => toggleMobileDropdown(key)}
        sx={mobileNavItemSx}
      >
        <ListItemText primary={label} />
        {mobileDropdowns[key] ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={mobileDropdowns[key]}>
        <List disablePadding>
          {dropdownMenus[key].map((item) => (
            <ListItemButton
              key={item.name}
              component={Link}
              to={item.path}
              onClick={closeDrawer}
              sx={mobileDropdownItemSx}
            >
              <ListItemText primary={item.name} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </React.Fragment>
  );

  const drawerContent = (
    <Box
      sx={{
        width: 330,
        maxWidth: "88vw",
        minHeight: "100vh",
        p: 2.5,
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(160deg, #071525 0%, #082d63 60%, #06111f 100%)",

        "&::before": {
          content: '""',
          position: "absolute",
          width: 180,
          height: 180,
          right: -60,
          top: -60,
          borderRadius: "50%",
          background: "rgba(38,198,91,0.22)",
          filter: "blur(10px)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box
          component={Link}
          to="/"
          onClick={closeDrawer}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            textDecoration: "none",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="ZENCON Logo"
            sx={{ height: 38 }}
          />
        </Box>

        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ position: "relative" }}>
        {mainLinks.map((link) => (
          <ListItemButton
            key={link.name}
            component={Link}
            to={link.path}
            onClick={closeDrawer}
            sx={mobileNavItemSx}
          >
            <ListItemText primary={link.name} />
          </ListItemButton>
        ))}

        {renderMobileDropdown("Services", "services")}
        {renderMobileDropdown("Products", "products")}

        <ListItemButton
          component={Link}
          to="/contact-us"
          onClick={closeDrawer}
          sx={mobileNavItemSx}
        >
          <ListItemText primary="Contact Us" />
        </ListItemButton>

        <Button
          component={Link}
          to="/smart-supply-chain-page"
          onClick={closeDrawer}
          startIcon={<AutoAwesomeIcon />}
          fullWidth
          sx={{
            ...smartSapButtonSx,
            ml: 0,
            mt: 2,
            justifyContent: "center",
          }}
        >
          Smart SAP
        </Button>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        left: 0,
        right: 0,
        boxShadow: "none",
        border: "none",
        background: isScrolled
          ? "linear-gradient(180deg, rgba(7,21,37,0.92) 0%, rgba(8,34,68,0.90) 100%)"
          : "transparent",
        backdropFilter: isScrolled ? "blur(14px)" : "none",
        transition: "background 0.8s ease, backdrop-filter 0.8s ease",

        "&::before, &::after": {
          display: "none",
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 72, md: 82 },
            justifyContent: "space-between",
          }}
        >
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
              cursor: "pointer",
              transition: "transform 0.35s ease",

              "&:hover": {
                transform: "scale(1.03)",
              },
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="ZENCON Logo"
              sx={{ height: 34 }}
            />
          </Box>

          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {mainLinks.map((link) => (
              <Button
                key={link.name}
                component={Link}
                to={link.path}
                sx={navButtonSx}
              >
                {link.name}
              </Button>
            ))}

            {renderDesktopDropdownButton("Services", "services")}
            {renderDesktopDropdownButton("Products", "products")}

            <Button component={Link} to="/contact-us" sx={navButtonSx}>
              Contact Us
            </Button>

            <Button
              component={Link}
              to="/smart-supply-chain-page"
              startIcon={<AutoAwesomeIcon />}
              sx={smartSapButtonSx}
            >
              Smart SAP
            </Button>
          </Box>

          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{
              display: { lg: "none" },
              color: "#fff",
              borderRadius: "14px",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",

              "&:hover": {
                background: "rgba(255,255,255,0.14)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Menu
        anchorEl={menuAnchor.element}
        open={desktopMenuOpen}
        onClose={closeDesktopMenu}
        PaperProps={{ sx: premiumMenuPaperSx }}
        MenuListProps={{
          onMouseLeave: closeDesktopMenu,
          sx: {
            p: 1,
          },
        }}
      >
        {(dropdownMenus[menuAnchor.key] || []).map((item) => (
          <MenuItem
            key={item.name}
            component={Link}
            to={item.path}
            onClick={closeDesktopMenu}
            sx={{
              borderRadius: "10px",
              mx: 0.8,
              my: 0.5,
              color: "#0f172a",
              fontWeight: 500,
              transition: "all 0.35s ease",

              "&:hover": {
                background:
                  "linear-gradient(135deg, #0755c9 0%, #0a6cff 50%, #3b82f6 100%)",
                color: "#fff",
                transform: "translateX(4px)",
                boxShadow: "0 6px 18px rgba(7, 85, 201, 0.35)",
              },
            }}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            background: "transparent",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
}
